import { FailedSendStatus } from '@/classes/SubmissionStatuses/FailedSendStatus'
import { ManuallyResolvedStatus } from '@/classes/SubmissionStatuses/ManuallyResolvedStatus'
import { NoneSentStatus } from '@/classes/SubmissionStatuses/NoneSentStatus'
import { ErrorStatus } from '@/classes/SubmissionStatuses/OapStatuses/ErrorStatus'
import { MalformedResponseStatus } from '@/classes/SubmissionStatuses/OapStatuses/MalformedResponseStatus'
import { PaymentErrorStatus } from '@/classes/SubmissionStatuses/OapStatuses/PaymentErrorStatus'
import { SuccessStatus } from '@/classes/SubmissionStatuses/OapStatuses/SuccessStatus'
import type StatusObject from '@/classes/SubmissionStatuses/StatusObject'
import {
	type LocalSubmissionStatus,
	SubmissionStatusMapper,
} from '@/classes/SubmissionStatuses/SubmissionStatusMapper'
import { UnknownStatus } from '@/classes/SubmissionStatuses/UnknownStatus'
import {
	type OapSubmissionStatus,
	type SubmissionAction,
} from '@/classes/SubmissionStatuses/types'

enum OapStatus {
	SUBMITTED = 'submitted',
	FAILED = 'failed',
	ERROR_PROCESSING_SUBMISSION = 'error_processing_submission',
	ERROR_PROCESSING_PAYMENT = 'error_processing_payment',
	DUPLICATE_AFTER_SUCCESS = 'duplicate_after_success',
	MANUALLY_RESOLVED = 'manually_resolved',
	MALFORMED_RESPONSE = 'malformed_response',
}

export class OapStatusMapper extends SubmissionStatusMapper {
	submissionStatusObject: StatusObject

	constructor(
		submissionStatus?: OapSubmissionStatus,
		localSubmissionStatuses: LocalSubmissionStatus[] = []
	) {
		super(localSubmissionStatuses)
		this.submissionStatusObject = this.toStatusObject(submissionStatus)
	}

	toStatusObject(submissionStatus?: OapSubmissionStatus): StatusObject {
		const failedChannels = this.getFailedOutgoingChannels(['Sepio', 'Inman'])

		if (failedChannels.length && !submissionStatus) {
			return new FailedSendStatus('OAP', failedChannels)
		}

		if (!submissionStatus) {
			return new NoneSentStatus('OAP')
		}

		switch (submissionStatus.status) {
			case OapStatus.SUBMITTED:
				return new SuccessStatus(submissionStatus)
			case OapStatus.MANUALLY_RESOLVED:
				return new ManuallyResolvedStatus(
					submissionStatus,
					submissionStatus.provider
				)
			case OapStatus.FAILED:
			case OapStatus.ERROR_PROCESSING_SUBMISSION:
				return new ErrorStatus(submissionStatus)
			case OapStatus.ERROR_PROCESSING_PAYMENT:
				return new PaymentErrorStatus(submissionStatus)
			case OapStatus.MALFORMED_RESPONSE:
				return new MalformedResponseStatus(submissionStatus)
			case OapStatus.DUPLICATE_AFTER_SUCCESS:
			default:
				return new UnknownStatus(submissionStatus)
		}
	}

	getVendorName() {
		return this.submissionStatusObject.getVendorName()
	}

	getThemeVariant() {
		return this.submissionStatusObject.getThemeVariant()
	}

	getActions(): Array<SubmissionAction> {
		return this.submissionStatusObject.getActions()
	}

	getStatusText(): string {
		return this.submissionStatusObject.getStatusText()
	}

	getToolTipText() {
		return this.submissionStatusObject.getToolTipText()
	}

	getUpdatedAt(): string {
		return this.submissionStatusObject.getUpdatedAt()
	}
}
